import React, { useState, useContext, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  IconButton,
  Tooltip,
  LinearProgress,
  Divider,
  Avatar,
  alpha,
  useTheme,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AccountBalanceWallet as WalletIcon,
  Savings as SavingsIcon,
  CreditCard as CreditCardIcon,
  History as HistoryIcon,
  Category as CategoryIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ShoppingCart as ShoppingIcon,
  Restaurant as DiningIcon,
  LocalGasStation as FuelIcon,
  Flight as TravelIcon,
  ShoppingBag as ShoppingBagIcon,
  Movie as EntertainmentIcon,
  Home as UtilitiesIcon,
  Language as OnlineShoppingIcon,
  Public as InternationalIcon,
  MoreHoriz as OtherIcon,
  ShowChart as ChartIcon,
  PieChart as PieChartIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { transactionService, Transaction, SavingsAnalysis } from '../services/transactions';
import { cardService } from '../services/cards';
import { AuthContext } from '../App';
import { format } from 'date-fns';

// Category icons mapping
const categoryIcons: { [key: string]: React.ReactElement } = {
  DINING: <DiningIcon />,
  GROCERIES: <ShoppingIcon />,
  FUEL: <FuelIcon />,
  TRAVEL: <TravelIcon />,
  SHOPPING: <ShoppingBagIcon />,
  ENTERTAINMENT: <EntertainmentIcon />,
  UTILITIES: <UtilitiesIcon />,
  ONLINE_SHOPPING: <OnlineShoppingIcon />,
  INTERNATIONAL: <InternationalIcon />,
  OTHER: <OtherIcon />,
};

// Category colors
const categoryColors: { [key: string]: string } = {
  DINING: '#ef4444',
  GROCERIES: '#10b981',
  FUEL: '#f59e0b',
  TRAVEL: '#3b82f6',
  SHOPPING: '#8b5cf6',
  ENTERTAINMENT: '#ec4899',
  UTILITIES: '#06b6d4',
  ONLINE_SHOPPING: '#6366f1',
  INTERNATIONAL: '#14b8a6',
  OTHER: '#6b7280',
};

// Chart colors
const CHART_COLORS = [
  '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#14b8a6', '#f97316', '#84cc16',
];

const Dashboard: React.FC = () => {
  const auth = useContext(AuthContext);
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [selectedPeriod, setSelectedPeriod] = useState<number>(30);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [transactionsLimit, setTransactionsLimit] = useState(10);

  // Fetch savings analysis
  const { data: savingsAnalysis, isLoading: savingsLoading, refetch: refetchSavings } = useQuery<SavingsAnalysis>({
    queryKey: ['savingsAnalysis', selectedPeriod],
    queryFn: () => transactionService.getSavingsAnalysis(selectedPeriod),
  });

  // Fetch user cards
  const { data: userCards = [] } = useQuery({
    queryKey: ['userCards'],
    queryFn: cardService.getUserCards,
  });

  // Fetch recent transactions
  const { data: transactionsData, isLoading: transactionsLoading, refetch: refetchTransactions } = useQuery({
    queryKey: ['transactions', { limit: transactionsLimit, category: selectedCategory, search: searchQuery }],
    queryFn: () => transactionService.getTransactions({
      limit: transactionsLimit,
      category: selectedCategory !== 'all' ? selectedCategory : undefined,
      page_size: 1000, // Get more for charts
    }),
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['savingsAnalysis'] });
    queryClient.invalidateQueries({ queryKey: ['transactions'] });
    refetchSavings();
    refetchTransactions();
  };

  // Prepare chart data
  const chartData = useMemo(() => {
    const transactions = transactionsData?.transactions || [];
    
    // Daily spending trend
    const dailySpending: { [key: string]: { date: string; amount: number; reward: number } } = {};
    transactions.forEach((txn: Transaction) => {
      if (txn.date) {
        const dateKey = format(new Date(txn.date), 'MMM dd');
        if (!dailySpending[dateKey]) {
          dailySpending[dateKey] = { date: dateKey, amount: 0, reward: 0 };
        }
        dailySpending[dateKey].amount += txn.amount;
        dailySpending[dateKey].reward += txn.reward_earned;
      }
    });
    const dailyData = Object.values(dailySpending)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(-14); // Last 14 days

    // Monthly spending
    const monthlySpending: { [key: string]: { month: string; spent: number; reward: number } } = {};
    transactions.forEach((txn: Transaction) => {
      if (txn.date) {
        const monthKey = format(new Date(txn.date), 'MMM yyyy');
        if (!monthlySpending[monthKey]) {
          monthlySpending[monthKey] = { month: monthKey, spent: 0, reward: 0 };
        }
        monthlySpending[monthKey].spent += txn.amount;
        monthlySpending[monthKey].reward += txn.reward_earned;
      }
    });
    const monthlyData = Object.values(monthlySpending)
      .sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime())
      .slice(-6); // Last 6 months

    return { dailyData, monthlyData };
  }, [transactionsData]);

  if (savingsLoading || transactionsLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress size={48} />
        </Box>
      </Container>
    );
  }

  const stats = savingsAnalysis || {
    total_spent: 0,
    total_reward_earned: 0,
    total_potential_reward: 0,
    total_missed_savings: 0,
    projected_annual_savings: 0,
    by_category: {},
  };

  const transactions = transactionsData?.transactions || [];
  const filteredTransactions = transactions.filter((txn: Transaction) => {
    if (searchQuery) {
      return (
        txn.merchant.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (txn.description && txn.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    return true;
  });

  // Calculate category totals for visualization
  const categoryTotals = Object.entries(stats.by_category || {}).map(([key, value]: [string, any]) => ({
    category: key,
    name: value.name || key,
    total_spent: value.total_spent || 0,
    reward_earned: value.reward_earned || 0,
    transaction_count: value.transaction_count || 0,
  })).sort((a, b) => b.total_spent - a.total_spent);

  const totalCategorySpending = categoryTotals.reduce((sum, cat) => sum + cat.total_spent, 0);

  // Pie chart data
  const pieChartData = categoryTotals.slice(0, 8).map((cat, index) => ({
    name: cat.name,
    value: cat.total_spent,
    color: CHART_COLORS[index % CHART_COLORS.length],
  }));

  const statCards = [
    {
      title: 'Total Spent',
      value: `TT$${stats.total_spent.toFixed(2)}`,
      icon: <WalletIcon sx={{ fontSize: 40 }} />,
      color: '#6366f1',
      gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      change: null,
    },
    {
      title: 'Rewards Earned',
      value: `TT$${stats.total_reward_earned.toFixed(2)}`,
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      change: stats.total_potential_reward > 0 ? `${((stats.total_reward_earned / stats.total_potential_reward) * 100).toFixed(1)}%` : null,
    },
    {
      title: 'Potential Savings',
      value: `TT$${stats.total_missed_savings.toFixed(2)}`,
      icon: <SavingsIcon sx={{ fontSize: 40 }} />,
      color: '#ec4899',
      gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
      change: stats.total_missed_savings > 0 ? 'Opportunity' : null,
    },
    {
      title: 'Active Cards',
      value: userCards.length.toString(),
      icon: <CreditCardIcon sx={{ fontSize: 40 }} />,
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      change: null,
    },
  ];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 2, boxShadow: 3 }}>
          <Typography variant="body2" fontWeight={700} gutterBottom>{label}</Typography>
          {payload.map((entry: any, index: number) => (
            <Typography key={index} variant="body2" sx={{ color: entry.color }}>
              {entry.name}: TT${entry.value.toFixed(2)}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                boxShadow: '0 8px 24px rgba(99, 102, 241, 0.3)',
              }}
            >
              <TrendingUpIcon sx={{ color: 'white', fontSize: 36 }} />
            </Box>
            <Typography variant="h4" component="h1" gutterBottom fontWeight={800}>
              Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Welcome back, {auth?.user?.first_name || auth?.user?.username}! Here's your financial overview.
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Period</InputLabel>
              <Select
                value={selectedPeriod}
                label="Period"
                onChange={(e) => setSelectedPeriod(Number(e.target.value))}
              >
                <MenuItem value={7}>Last 7 days</MenuItem>
                <MenuItem value={30}>Last 30 days</MenuItem>
                <MenuItem value={90}>Last 90 days</MenuItem>
                <MenuItem value={365}>Last year</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Refresh Data">
              <IconButton onClick={handleRefresh} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: '100%',
                background: stat.gradient,
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: `0 16px 48px ${alpha(stat.color, 0.4)}`,
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100%',
                  height: '100%',
                  background: 'radial-gradient(circle at top right, rgba(255,255,255,0.2) 0%, transparent 50%)',
                  pointerEvents: 'none',
                },
              }}
            >
              <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ color: 'white', opacity: 0.9 }}>{stat.icon}</Box>
                  {stat.change && (
                    <Chip
                      label={stat.change.includes('%') ? `${stat.change}` : stat.change}
                      size="small"
                      sx={{
                        background: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        fontWeight: 600,
                        backdropFilter: 'blur(10px)',
                      }}
                    />
                  )}
                </Box>
                <Typography variant="h3" fontWeight={800} sx={{ color: 'white', mb: 0.5 }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  {stat.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Spending Trend Chart */}
        <Grid item xs={12} lg={8}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ChartIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Spending Trend
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Daily spending over the last 14 days
                  </Typography>
                </Box>
              </Box>
              {chartData.dailyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={chartData.dailyData}>
                    <defs>
                      <linearGradient id="colorSpending" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000', 0.1)} />
                    <XAxis dataKey="date" stroke={theme.palette.text.secondary} />
                    <YAxis stroke={theme.palette.text.secondary} />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="amount"
                      stroke="#6366f1"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorSpending)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  No spending data available for the selected period
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Category Breakdown Pie Chart */}
        <Grid item xs={12} lg={4}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PieChartIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Category Breakdown
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Spending by category
                  </Typography>
                </Box>
              </Box>
              {pieChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  No category data available
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Monthly Spending & Transactions Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Monthly Spending Bar Chart */}
        <Grid item xs={12} lg={6}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <TrendingUpIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Monthly Spending
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Last 6 months overview
                  </Typography>
                </Box>
              </Box>
              {chartData.monthlyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData.monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000', 0.1)} />
                    <XAxis dataKey="month" stroke={theme.palette.text.secondary} />
                    <YAxis stroke={theme.palette.text.secondary} />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Bar dataKey="spent" fill="#f59e0b" radius={[8, 8, 0, 0]} />
                    <Bar dataKey="reward" fill="#10b981" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  No monthly data available
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Rewards Comparison */}
        <Grid item xs={12} lg={6}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <SavingsIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Rewards Analysis
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Earned vs Potential rewards
                  </Typography>
                </Box>
              </Box>
              <Stack spacing={3}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" fontWeight={600}>Rewards Earned</Typography>
                    <Typography variant="body2" fontWeight={700} color="success.main">
                      TT${stats.total_reward_earned.toFixed(2)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={stats.total_potential_reward > 0 ? (stats.total_reward_earned / stats.total_potential_reward) * 100 : 0}
                    sx={{
                      height: 12,
                      borderRadius: 6,
                      backgroundColor: alpha('#10b981', 0.1),
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#10b981',
                        borderRadius: 6,
                      },
                    }}
                  />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" fontWeight={600}>Potential Rewards</Typography>
                    <Typography variant="body2" fontWeight={700} color="info.main">
                      TT${stats.total_potential_reward.toFixed(2)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={100}
                    sx={{
                      height: 12,
                      borderRadius: 6,
                      backgroundColor: alpha('#3b82f6', 0.1),
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#3b82f6',
                        borderRadius: 6,
                      },
                    }}
                  />
                </Box>
                {stats.total_missed_savings > 0 && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" fontWeight={600}>Missed Savings</Typography>
                      <Typography variant="body2" fontWeight={700} color="error.main">
                        TT${stats.total_missed_savings.toFixed(2)}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={100}
                      sx={{
                        height: 12,
                        borderRadius: 6,
                        backgroundColor: alpha('#ef4444', 0.1),
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: '#ef4444',
                          borderRadius: 6,
                        },
                      }}
                    />
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column - Transactions */}
        <Grid item xs={12} lg={8}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <HistoryIcon sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Recent Transactions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Your latest spending activity
                    </Typography>
                  </Box>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    window.open('/transactions', '_blank');
                  }}
                >
                  View All
                </Button>
              </Box>

              {/* Filters */}
              <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                <TextField
                  size="small"
                  placeholder="Search transactions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                  sx={{ flexGrow: 1 }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={selectedCategory}
                    label="Category"
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <MenuItem value="all">All Categories</MenuItem>
                    {Object.entries(stats.by_category || {}).map(([key, value]: [string, any]) => (
                      <MenuItem key={key} value={key}>{value.name || key}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              {/* Transactions Table */}
              {filteredTransactions.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  No transactions found. Upload a statement to get started!
                </Alert>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell><strong>Merchant</strong></TableCell>
                        <TableCell><strong>Category</strong></TableCell>
                        <TableCell align="right"><strong>Amount</strong></TableCell>
                        <TableCell align="right"><strong>Reward</strong></TableCell>
                        <TableCell align="right"><strong>Card</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredTransactions.slice(0, transactionsLimit).map((txn: Transaction) => (
                        <TableRow
                          key={txn.id}
                          hover
                          sx={{
                            '&:hover': {
                              backgroundColor: alpha('#6366f1', 0.04),
                            },
                          }}
                        >
                          <TableCell>
                            {txn.date ? format(new Date(txn.date), 'MMM dd, yyyy') : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {txn.merchant}
                            </Typography>
                            {txn.description && (
                              <Typography variant="caption" color="text.secondary">
                                {txn.description.substring(0, 50)}
                                {txn.description.length > 50 ? '...' : ''}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={categoryIcons[txn.category] || <OtherIcon />}
                              label={txn.category.replace('_', ' ')}
                              size="small"
                              sx={{
                                backgroundColor: alpha(categoryColors[txn.category] || '#6b7280', 0.1),
                                color: categoryColors[txn.category] || '#6b7280',
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={700}>
                              TT${txn.amount.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {txn.reward_earned > 0 && (
                              <Chip
                                label={`TT$${txn.reward_earned.toFixed(2)}`}
                                size="small"
                                sx={{
                                  backgroundColor: alpha('#10b981', 0.1),
                                  color: '#10b981',
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {txn.card_name || 'N/A'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {filteredTransactions.length > transactionsLimit && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setTransactionsLimit(transactionsLimit + 10)}
                  >
                    Load More ({filteredTransactions.length - transactionsLimit} remaining)
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Category Breakdown & Insights */}
        <Grid item xs={12} lg={4}>
          <Stack spacing={3}>
            {/* Spending by Category */}
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CategoryIcon sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Spending by Category
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Last {selectedPeriod} days
                    </Typography>
                  </Box>
                </Box>

                {categoryTotals.length === 0 ? (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    No category data available
                  </Alert>
                ) : (
                  <Stack spacing={2}>
                    {categoryTotals.slice(0, 8).map((cat) => {
                      const percentage = totalCategorySpending > 0
                        ? (cat.total_spent / totalCategorySpending) * 100
                        : 0;
                      return (
                        <Box key={cat.category}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ color: categoryColors[cat.category] || '#6b7280' }}>
                                {categoryIcons[cat.category] || <OtherIcon />}
                              </Box>
                              <Typography variant="body2" fontWeight={600}>
                                {cat.name}
                              </Typography>
                            </Box>
                            <Typography variant="body2" fontWeight={700}>
                              TT${cat.total_spent.toFixed(2)}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={percentage}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: alpha(categoryColors[cat.category] || '#6b7280', 0.1),
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: categoryColors[cat.category] || '#6b7280',
                                borderRadius: 4,
                              },
                            }}
                          />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {cat.transaction_count} transactions
                            </Typography>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              {percentage.toFixed(1)}%
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </CardContent>
            </Card>

            {/* Savings Opportunity */}
            {stats.total_missed_savings > 0 && (
              <Card
                sx={{
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
                  boxShadow: '0 8px 24px rgba(236, 72, 153, 0.3)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <SavingsIcon sx={{ color: 'white', fontSize: 32 }} />
                    <Box>
                      <Typography variant="h6" fontWeight={700} color="white">
                        Savings Opportunity
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                        You could save more!
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="h4" fontWeight={800} color="white" gutterBottom>
                    TT${stats.total_missed_savings.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 2 }}>
                    Potential additional savings in the last {selectedPeriod} days by using optimal cards
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      background: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 700,
                      '&:hover': {
                        background: 'rgba(255,255,255,0.3)',
                      },
                    }}
                    onClick={() => window.location.href = '/smart-recommendations'}
                  >
                    View Recommendations
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Projected Annual Savings */}
            {stats.projected_annual_savings > 0 && (
              <Card
                sx={{
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  boxShadow: '0 8px 24px rgba(99, 102, 241, 0.3)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                    Projected Annual Savings
                  </Typography>
                  <Typography variant="h3" fontWeight={800} color="white" gutterBottom>
                    TT${stats.projected_annual_savings.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    Based on your current spending patterns, optimize your card usage to maximize rewards.
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
